// bm-design-system: auto-growing textarea primitive
//
// A controlled <textarea> whose height tracks its content — it grows to fit
// every line and never shows a vertical scrollbar. Use for plain-text fields
// where the body can be arbitrarily long (e.g. a piece's `content` field) and
// a fixed-height, scrolling box would hide most of what the operator wrote.
import * as React from "react";
import { cn } from "@/lib/utils";

// useLayoutEffect would warn during SSR (no DOM to measure); fall back to
// useEffect on the server. The measurement only matters on the client anyway.
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

export interface AutoGrowTextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {}

const AutoGrowTextarea = React.forwardRef<HTMLTextAreaElement, AutoGrowTextareaProps>(
  ({ className, value, onChange, ...props }, forwardedRef) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);

    const setRef = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node;
        if (typeof forwardedRef === "function") forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      },
      [forwardedRef],
    );

    const resize = React.useCallback(() => {
      const el = innerRef.current;
      if (!el) return;
      // Reset first so the textarea can shrink as well as grow, then size to
      // the scroll height. Add the vertical border (offset − client) so the
      // border-box height fits the content exactly with no residual scroll.
      el.style.height = "auto";
      const border = el.offsetHeight - el.clientHeight;
      el.style.height = `${el.scrollHeight + border}px`;
    }, []);

    // Re-measure whenever the value changes (covers programmatic updates too).
    useIsomorphicLayoutEffect(resize, [resize, value]);

    return (
      <textarea
        ref={setRef}
        className={cn("form-control resize-none overflow-hidden", className)}
        value={value}
        onChange={(e) => {
          onChange?.(e);
          resize();
        }}
        {...props}
      />
    );
  },
);
AutoGrowTextarea.displayName = "AutoGrowTextarea";

export { AutoGrowTextarea };
